import { IonBadge, IonChip, IonCol, IonGrid, IonIcon, IonItem, IonRippleEffect, IonRow, IonText } from '@ionic/react';
import { business, helpCircle, logIn, logOut, person } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { getStringDateTime } from '../../common/util';
import { IRepairOrder } from '../../models/dms/repairOrder.model';
import { IonCardWrapper } from './RepairOrderCard.styles';
import FieldDetailRowGroup, { IFieldDetailRow } from '../detailRow/FieldDetailRowGroup';

interface IComponentProps {
  repairOrder: IRepairOrder;
  onClick: (e: React.MouseEvent, repairOrder: IRepairOrder) => void;
}

const RepairOrderCard: React.FC<IComponentProps> = ({ repairOrder, onClick }) => {
  const [status, setStatus] = useState('');
  const [fieldRows, setFieldRows] = useState([] as IFieldDetailRow[]);

  useEffect(() => {
    if (repairOrder.isClosed) {
      setStatus('CLOSED');
    } else if (!repairOrder.isConfirmed) {
      setStatus('UNCONFIRMED');
    } else if (repairOrder.isInvoiced) {
      setStatus('INVOICED');
    } else if (repairOrder.isQuotation) {
      setStatus('QUOTATION');
    } else if (!repairOrder.isVetted) {
      setStatus('UNVETTED');
    } else {
      setStatus('');
    }

    let fieldDetailRows = [] as IFieldDetailRow[];
    if (repairOrder.driver?.name) {
      fieldDetailRows.push({ value: repairOrder.driver.name, icon: person, lines: false, slim: true });
    }
    if (repairOrder.driver.companyName) {
      fieldDetailRows.push({ value: repairOrder.driver.companyName, icon: business, lines: false, slim: true });
    }
    if (!repairOrder.driver?.name && !repairOrder.driver.companyName) {
      fieldDetailRows.push({ value: 'NO DRIVER FOUND', icon: helpCircle, lines: false, slim: true });
    }
    setFieldRows(fieldDetailRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairOrder]);

  const getRoDisplayDate = (inputDate: string) => {
    return getStringDateTime(inputDate).format('MMM DD, YYYY HH:mm');
  };

  return (
    <IonCardWrapper className="ion-activatable ripple-card flexed-fill-auto" onClick={(e) => onClick(e, repairOrder)}>
      <IonRippleEffect type="bounded" />

      {status && <div className="disable-card"></div>}
      {status && (
        <div className="flexed status-label">
          <IonBadge color="tertiary" slot="end">
            {status}
          </IonBadge>
        </div>
      )}

      <IonItem lines="none" className="ion-margin-top">
        <FieldDetailRowGroup rows={fieldRows} />
      </IonItem>

      <IonItem lines="none">
        <IonGrid className="ion-no-padding ion-no-margin">
          <IonRow>
            <IonCol>
              <IonItem lines="none" className="ion-no-padding ion-no-margin">
                <IonGrid>
                  <IonRow>
                    <IonCol className="ion-no-padding ion-no-margin">
                      <div className="ro">
                        {repairOrder.branch} / {repairOrder.repairOrderNumber}
                      </div>
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    <IonCol className="ion-no-padding ion-no-margin">
                      <div className="status">{repairOrder.progressStatus?.description}</div>
                    </IonCol>
                  </IonRow>

                  {repairOrder.vehicle?.fleetNumber && <IonChip className="ro-data">{repairOrder.vehicle?.fleetNumber}</IonChip>}
                </IonGrid>
              </IonItem>
            </IonCol>
            <IonCol className="vehicle-col">
              {(() => {
                switch (repairOrder.vehicle?.source) {
                  case 'VHS':
                    return (
                      <IonItem lines="none" className="ion-no-padding ion-no-margin">
                        <IonGrid>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="veh-source">Stock Unit</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="rego">{repairOrder.vehicle.stockNumber}</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="vehicle">{repairOrder.vehicle?.description}</div>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonItem>
                    );
                  case 'WKV':
                    return (
                      <IonItem lines="none" className="ion-no-padding ion-no-margin">
                        <IonGrid>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="veh-source">Workshop Vehicle</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="rego">{repairOrder.registrationNumber}</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="vehicle">{repairOrder.vehicle?.description}</div>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonItem>
                    );
                  case 'FAS':
                    return (
                      <IonItem lines="none" className="ion-no-padding ion-no-margin">
                        <IonGrid>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="veh-source">Fixed Asset</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="rego">{repairOrder.vehicle.sourceRef}</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="vehicle">{repairOrder.vehicle?.description}</div>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonItem>
                    );
                  case 'PAR':
                    return (
                      <IonItem lines="none" className="ion-no-padding ion-no-margin">
                        <IonGrid>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="veh-source">Part</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="rego">{repairOrder.vehicle.sourceRef}</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="vehicle">{repairOrder.vehicle?.description}</div>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonItem>
                    );
                  default:
                    return (
                      <IonItem lines="none" className="ion-no-padding ion-no-margin">
                        <IonGrid>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="veh-source">Other Vehicle</div>
                            </IonCol>
                          </IonRow>
                          <IonRow>
                            {repairOrder.registrationNumber ? (
                              <IonCol className="ion-no-padding ion-no-margin">
                                <div className="rego">{repairOrder.registrationNumber}</div>
                              </IonCol>
                            ) : (
                              <IonCol className="ion-no-padding ion-no-margin">
                                <div className="rego">{repairOrder.vehicle.stockNumber}</div>
                              </IonCol>
                            )}
                          </IonRow>
                          <IonRow>
                            <IonCol className="ion-no-padding ion-no-margin">
                              <div className="vehicle">{repairOrder.vehicle?.description}</div>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonItem>
                    );
                }
              })()}
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonItem>
      <div className="flexed inout-label">
        <IonBadge color="light" class="flexed flexed-center">
          <IonIcon icon={logIn} color="primarylight" />
          <IonText>{getRoDisplayDate(repairOrder.dropOffDateTime)}</IonText>
        </IonBadge>
        <IonBadge color="light" class="flexed flexed-center">
          <IonIcon icon={logOut} color="primarylight" />
          <IonText>{getRoDisplayDate(repairOrder.pickUpDateTime)}</IonText>
        </IonBadge>
      </div>
    </IonCardWrapper>
  );
};

export default RepairOrderCard;
